export type SlaUrgency = 'none' | 'on_track' | 'due_soon' | 'overdue'

export type SlaStatusInfo = {
  urgency: SlaUrgency
  label: string
  detail?: string
  progressPercent: number | null
}

const DUE_SOON_MS = 24 * 60 * 60 * 1000

export function getSlaStatus(slaDueAt: string | null | undefined, now = Date.now()): SlaStatusInfo {
  if (!slaDueAt) {
    return { urgency: 'none', label: 'No SLA target', progressPercent: null }
  }

  const due = new Date(slaDueAt).getTime()
  if (Number.isNaN(due)) {
    return { urgency: 'none', label: 'SLA unavailable', progressPercent: null }
  }

  const remaining = due - now
  const detail = new Date(slaDueAt).toLocaleString('en-PH', {
    timeZone: 'Asia/Manila',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })

  if (remaining <= 0) {
    const overdueMs = Math.abs(remaining)
    return {
      urgency: 'overdue',
      label: formatRelativeDuration(overdueMs, 'overdue'),
      detail: `Target was ${detail}`,
      progressPercent: 100,
    }
  }

  if (remaining <= DUE_SOON_MS) {
    return {
      urgency: 'due_soon',
      label: formatRelativeDuration(remaining, 'due'),
      detail: `Due ${detail}`,
      progressPercent: Math.min(99, Math.round((1 - remaining / DUE_SOON_MS) * 100)),
    }
  }

  return {
    urgency: 'on_track',
    label: formatRelativeDuration(remaining, 'due'),
    detail: `Due ${detail}`,
    progressPercent: Math.max(5, Math.min(85, 50)),
  }
}

function formatRelativeDuration(ms: number, mode: 'due' | 'overdue'): string {
  const abs = Math.abs(ms)
  const minutes = Math.floor(abs / 60000)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days >= 1) {
    const remH = hours % 24
    const unit = `${days}d${remH > 0 ? ` ${remH}h` : ''}`
    return mode === 'overdue' ? `Overdue by ${unit}` : `Due in ${unit}`
  }
  if (hours >= 1) {
    const remM = minutes % 60
    const unit = `${hours}h${remM > 0 ? ` ${remM}m` : ''}`
    return mode === 'overdue' ? `Overdue by ${unit}` : `Due in ${unit}`
  }
  const unit = `${Math.max(1, minutes)}m`
  return mode === 'overdue' ? `Overdue by ${unit}` : `Due in ${unit}`
}
